import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaPlus } from 'react-icons/fa';

import { addCourseTypeApi, getCourseTypeApi, updateCourseTypeApi } from '../../../Api/CourseType';

interface Props {
    url: string;
    onClose: () => void;
    onSaved?: () => void;
    editId: string | null;
}

type CourseTypeInterface = {
    id: string;
    name: string;
    parentId: string | null;
};
type TimingInterface = {
    id: string | null;
    time: string;
};
type CustomTime = {
    id: string | null;
    customStartTime: string;
    customEndTime: string;
    courseId: string | null;
};

export function CourseTypeForm({ url, onClose, editId , onSaved }: Props) {
    const navigate = useNavigate();
    const location = useLocation();
    const id = editId;

    const [CourseType, setCourseType] = useState<CourseTypeInterface[]>([]);

    const [DurationTypeOptions, setDurationTypeOptions] = useState([
        "Monthly",
        "Yearly",
        "Quarterly",
        "Life time"
    ]);

    const [Type, setType] = useState<string | undefined>("");
    const [Name, setName] = useState<string | undefined>("");
    const [DurationType, setDurationType] = useState<string | undefined>("");
    const [CustomDurationType, setCustomDurationType] = useState<
        string | undefined
    >("");
    const [Fees, setFees] = useState<string | number | undefined>("");

    //Time Slots for Availability
    //Parent
    const [Availability, setAvailability] = useState('');
    //Child for Fixed Duration
    const [FlexibleDuration, setFlexibleDuration] = useState('');
    const [FlexibleDurationFor24_7, setFlexibleDurationFor24_7] = useState<TimingInterface[]>([]);;
    const [MultipleAvailability, setMultipleAvailability] = useState<TimingInterface[]>([]);
    const [customTime, setCustomTime] = useState<CustomTime[]>([{ id: null, customStartTime: '', customEndTime: '', courseId: null }]);

    const [otherCheckbox, setOtherCheckbox] = useState(false);
    const [timingsToDelete, setTimingsToDelete] = useState<string[]>([]);

    const [isEdit, setIsEditMode] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [Id, setId] = useState('');

    const addTimeInput = () => {

        const Timing: TimingInterface = {
            id: null,
            time: `${customTime[0]?.customStartTime}-${customTime[0]?.customEndTime}`,

        };
        setMultipleAvailability([...MultipleAvailability, Timing]);
        setCustomTime([{ id: null, customStartTime: '', customEndTime: '', courseId: null }]);
    };

    useEffect(() => {
        const handleApiClick = async () => {
            const courseTypeData: any = await getCourseTypeApi(url);
            setCourseType(courseTypeData as CourseTypeInterface[]);
            if (id) {
                const courseTypeToEdit = courseTypeData.find((courseType: any) => courseType.id === id);
                if (courseTypeToEdit) {
                    setId(courseTypeToEdit.id);
                    setName(courseTypeToEdit.name ?? "");
                    setType(courseTypeToEdit.parentId ?? "");
                    setFees(courseTypeToEdit.fees ?? "");
                    setOtherCheckbox(courseTypeToEdit.other ?? false);
                    if (courseTypeToEdit.availability != null && courseTypeToEdit.availability === "Fixed") {
                        setAvailability('Fixed');
                        setMultipleAvailability(courseTypeToEdit.timings.map((item: any) => ({
                            id: item.id,
                            time: item.startime.slice(0, -3) + '-' + item.endtime.slice(0, -3)
                        })));
                    } else if (courseTypeToEdit.availability != null && courseTypeToEdit.availability === "Flexible") {
                        setAvailability('Flexible');
                        if (courseTypeToEdit.timings && courseTypeToEdit.timings.length > 0) {
                            if (courseTypeToEdit.timings[0].startime === "00:00:00" && courseTypeToEdit.timings[0].endtime === "23:59:00") {
                                setFlexibleDuration('24/7');
                                setFlexibleDurationFor24_7([{ id: courseTypeToEdit.timings[0].id, time: '24/7' }]);
                            } else {
                                setFlexibleDuration('custom');
                                setMultipleAvailability(courseTypeToEdit.timings.map((item: any) => ({
                                    id: item.id,
                                    time: item.startime.slice(0, -3) + '-' + item.endtime.slice(0, -3)
                                })));
                            }
                        }
                    }
                    if (courseTypeToEdit.durationType === null) {
                        setDurationType("");
                    }
                    else if (DurationTypeOptions.includes(courseTypeToEdit.durationType)) {
                        setDurationType(courseTypeToEdit.durationType);
                    } else {
                        setDurationType("Custom");
                        setCustomDurationType(courseTypeToEdit.durationType);
                    }

                    setIsEditMode(true);
                }
            }
        };
        handleApiClick();
    }, [id, navigate]);

    const handleResetForm = () => {
        setName("");
        setType("");
        setId("");
        setDurationType("");
        setCustomDurationType("");
        setFees("");
        setAvailability("");
        setCustomTime([{ id: null, customStartTime: '', customEndTime: '', courseId: null }]);
        onClose();
    };

    const handleSubmitClick = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!Name) {
            toast.error("Please fill all the fields");
            return;
        }
        if (Availability === 'Fixed' && MultipleAvailability.length === 0) {
            toast.error("Please fill all the fields");
            return
        } else if (Availability === 'Flexible' && FlexibleDuration === 'custom' && MultipleAvailability.length === 0) {
            toast.error("Please fill all the fields");
            return
        }

        const finalDurationType = DurationType === "Custom" ? CustomDurationType : DurationType ? DurationType : null;

        try {
            if (isEdit) {
                let finalTimings: { id: string | null; time: string; }[];

                if (Availability === 'Fixed') {
                    finalTimings = MultipleAvailability;
                } else if (Availability === 'Flexible') {
                    finalTimings = FlexibleDuration === '24/7'
                        ? [{ id: FlexibleDurationFor24_7[0]?.id, time: '00:00-23:59' }]
                        : MultipleAvailability;
                } else {
                    finalTimings = [];
                }

                // Filter out undefined values
                const filteredAvailabilitysToDelete = timingsToDelete.filter(id => id !== undefined && id !== null);

                await updateCourseTypeApi(Id, Type, Name, finalDurationType, Fees !== "" ? Number(Fees) : null, Availability, otherCheckbox, finalTimings, filteredAvailabilitysToDelete, url);
                toast.success('Course Type updated successfully');
            } else {
                let finalTimings: string[];
                // if (Availability === 'Fixed') {
                //     finalTimings = MultipleAvailability.map(item => item.availability);
                // } else if (Availability === 'Flexible') {
                //     finalTimings = FlexibleDuration === '24/7' ? ['24/7'] : [`${customTime[0]?.customStartTime}-${customTime[0]?.customEndTime}`];
                // } else {
                //     finalTimings = [];
                // }
                if (Availability === 'Fixed' || (Availability === 'Flexible' && FlexibleDuration === 'custom')) {
                    finalTimings = MultipleAvailability.map(item => item.time);
                } else if (Availability === 'Flexible' && FlexibleDuration === '24/7') {
                    finalTimings = ['00:00-23:59'];
                }
                await addCourseTypeApi(Type, Name, finalDurationType, Fees !== "" ? Number(Fees) : null, Availability, otherCheckbox, finalTimings, url);
                toast.success('Course Type added successfully');
            }
            
            navigate("/admin/CourseType");
            if (onSaved) onSaved();
            onClose();

        } catch (error) {
            console.error("Error handling submit:", error);
            toast.error(
                isEdit ? "Failed to update Course Type" : "Unable to add Course Type"
            );
        }
    };

    const handleDeleteMultipleAvailability = async (deleteindex) => {

        if (isEdit === true) {
            const id = MultipleAvailability[deleteindex].id;
            setTimingsToDelete([...timingsToDelete, id]);

            const updatedAvailability = MultipleAvailability.filter((_, i) => i !== deleteindex);
            setMultipleAvailability(updatedAvailability);
            // const updatedAvailability = MultipleAvailability.map((item, index) => {
            //     if (index === deleteindex) {
            //         return
            // 
            // 
            // { ...item, isDelete: true };
            //     }
            //     return item;
            // });
        }
        else {
            const updatedAvailability = MultipleAvailability.filter((_, i) => i !== deleteindex);
            setMultipleAvailability(updatedAvailability);
        }

    };


    return (
        <div className="max-w-6xl">
            <div
                className={`transition duration-300 ${isModalOpen ? "filter blur-sm" : ""
                    }`}
            >
                <h2 className="text-3xl font-bold text-blue-600 mb-5">Course Type Form</h2>
                <form onSubmit={handleSubmitClick} className="mt-8 space-y-4">
                    <div className="flex flex-col gap-4">


                        {/* <div className="flex flex-col space-y-2">
                     <label className="font-medium text-black dark:text-gray-200">Course Type</label>
                     <select
                         className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500"
                         value={Type || ""}
                         onChange={(e) => {
                             setType(e.target.value);
                         }}
                     >
                         <option value="" disabled>Select Course Type</option>
                         {filteredCourseType.map((item) => (
                             <option key={item.id} value={item.id}>
                                 {item.name}
                             </option>
                         ))}
                     </select>
                    </div> */}

                        <div>
                            <label htmlFor="courseType" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="Name"
                                type="text"
                                required
                                placeholder="Name"
                                value={Name ?? ""}
                                onChange={(event) => setName(event.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="durationType" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                Duration Type
                            </label>
                            <select
                                value={DurationType}
                                onChange={(event) => setDurationType(event.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select Duration Type</option>
                                {DurationTypeOptions.map((item) => (
                                    <option key={item} value={item}>
                                        {item}
                                    </option>
                                ))}
                                {/* <option value="Custom">Custom</option> */}
                            </select>
                            {DurationType === "Custom" && (
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Enter custom duration type"
                                        value={CustomDurationType}
                                        onChange={(event) => setCustomDurationType(event.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}

                        </div>
                        <div>
                            <label htmlFor="fees" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                Fees
                            </label>
                            <input
                                id="fees"
                                type="number"
                                placeholder="Fees"
                                value={Fees ?? ""}
                                onChange={(event) => setFees(event.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div >
                            <label htmlFor="Availability" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                Availability
                            </label>
                            <div className="mt-2">
                                <select
                                    value={Availability}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        if (isEdit === true) {
                                            const idsToDelete = MultipleAvailability.map(item => item.id).filter(id => id !== undefined);
                                            const flexibleIdsToDelete = FlexibleDurationFor24_7.map(item => item.id).filter(id => id !== undefined);
                                            const customTimeIdsToDelete = customTime.map(item => item.id).filter(id => id !== undefined);
                                            setTimingsToDelete(prev => [...prev, ...idsToDelete, ...flexibleIdsToDelete, ...customTimeIdsToDelete]);
                                            setMultipleAvailability([]);
                                            setFlexibleDurationFor24_7([]);

                                        }
                                        setAvailability(value);
                                        setFlexibleDuration('');
                                        setCustomTime([{ id: null, customStartTime: '', customEndTime: '', courseId: null }]);
                                    }}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select Availability</option>
                                    <option value="Flexible">Flexible Duration</option>
                                    <option value="Fixed">Fixed Durations</option>
                                </select>
                            </div>

                            {/* fixed duration selected  */}
                            {Availability === 'Flexible' && (
                                <div className="mt-2">
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            className="form-radio"
                                            name="Flexible"
                                            value="24/7"
                                            checked={FlexibleDuration === '24/7'}
                                            onChange={(e) => {
                                                if (isEdit === true) {
                                                    setTimingsToDelete([...timingsToDelete, customTime[0]?.id]);
                                                    setCustomTime([{ id: null, customStartTime: '', customEndTime: '', courseId: null }]);
                                                    const idsToDelete = MultipleAvailability.map(item => item.id).filter(id => id !== undefined);
                                                    setTimingsToDelete(prev => [...prev, ...idsToDelete]);
                                                }
                                                setFlexibleDuration(e.target.value)
                                            }}
                                        />
                                        <span className="ml-2">24/7  </span>
                                    </label>
                                    <label className="inline-flex items-center ml-6">
                                        <input
                                            type="radio"
                                            className="form-radio"
                                            name="Flexible"
                                            value="custom"
                                            checked={FlexibleDuration === 'custom'}
                                            onChange={(e) => {
                                                if (isEdit === true) {
                                                    setTimingsToDelete([...timingsToDelete, FlexibleDurationFor24_7[0]?.id]);
                                                    setFlexibleDurationFor24_7([]);

                                                    const idsToDelete = MultipleAvailability.map(item => item.id).filter(id => id !== undefined);
                                                    setTimingsToDelete(prev => [...prev, ...idsToDelete]);

                                                    setMultipleAvailability([]);
                                                }
                                                setFlexibleDuration(e.target.value)
                                            }}
                                        />
                                        <span className="ml-2">Custom Duration </span>
                                    </label>
                                </div>
                            )}


                        </div>

                        {/* on selecting Custom Duration in Fixed  */}
                        {/* {FlexibleDuration === 'custom' && (
                         <div className="mb-4">
                             <div className="flex justify-between">
                                 <div className="w-1/2 pr-2">
                                     <label htmlFor="customStartTime" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                         Start Time
                                     </label>
                                     <input
                                         type="time"
                                         id="customStartTime"
                                         value={customTime[0]?.customStartTime}
                                         onChange={(e) => setCustomTime([{ ...customTime[0], customStartTime: e.target.value }])}
                                         className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                         required={Availability === 'Fixed'}
                                     />
                                 </div>
                                 <div className="w-1/2 pl-2">
                                     <label htmlFor="customEndTime" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                         End Time
                                     </label>
                                     <input
                                         type="time"
                                         id="customEndTime"
                                         value={customTime[0]?.customEndTime}
                                         onChange={(e) => setCustomTime([{ ...customTime[0], customEndTime: e.target.value }])}
                                         className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                         required={Availability === 'Fixed'}
                                     />
                                 </div>
                             </div>

                         </div>
                     )} */}

                        {(Availability === 'Fixed' || FlexibleDuration === 'custom') && (
                            <div className="mb-4">

                                <button
                                    type='button'
                                    onClick={() => setIsModalOpen(true)}
                                    className="mt-4 flex items-center justify-center px-4 py-2 text-sm tracking-wide text-white capitalize transition-colors duration-300 transform bg-blue-600 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-80"
                                >
                                    <FaPlus className="mr-2 w-4" />
                                    Add Time
                                </button>
                                {MultipleAvailability.length > 0 && (
                                    <div>


                                        <table className="mt-5 w-full border divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
                                            <thead className="bg-gray-100 dark:bg-gray-800">
                                                <tr>
                                                    <th className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center ">Action</th>
                                                    <th className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">Start Time</th>
                                                    <th className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">End Time</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                                                {MultipleAvailability.map((time, index) => (
                                                    <tr key={index}>
                                                        <td className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center text-gray-500">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeleteMultipleAvailability(index)}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    strokeWidth="1.5"
                                                                    stroke="currentColor"
                                                                    className="w-5 h-5"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                                                    />
                                                                </svg>
                                                            </button>
                                                        </td>
                                                        <td className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">{time.time && time.time.includes('-') ? time.time && time.time.split('-')[0] : time.time}</td>
                                                        <td className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">{time.time && time.time.includes('-') ? time.time && time.time.split('-')[1] : ''}</td>

                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <div className="mt-5 flex items-center ">
                                            <input
                                                checked={otherCheckbox}
                                                id="bordered-checkbox-1"
                                                type="checkbox" value=""
                                                onChange={(e) => {
                                                    setOtherCheckbox(e.target.checked);
                                                }}
                                                name="bordered-checkbox"
                                                className="w-4 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded   dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                            />
                                            <label htmlFor="bordered-checkbox-1" className="py-4 ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">Allow users to select other timming option while registration</label>
                                        </div>

                                    </div>
                                )}



                                {/* <ul className="mt-2 space-y-2">
                                 {MultipleAvailability.map((time, index) => (
                                     (
                                         <li key={index} className="flex max-w-40 justify-left gap-2 items-center bg-gray-100 p-2 rounded-lg">
                                             <span> {time.availability} </span>
                                             <button
                                                 type="button"
                                                 onClick={() => handleDeleteMultipleAvailability(index)}
                                                 className="text-red-500 hover:text-red-700"
                                             >
                                                 <svg
                                                     xmlns="http://www.w3.org/2000/svg"
                                                     fill="none"
                                                     viewBox="0 0 24 24"
                                                     strokeWidth="1.5"
                                                     stroke="currentColor"
                                                     className="w-5 h-5"
                                                 >
                                                     <path
                                                         strokeLinecap="round"
                                                         strokeLinejoin="round"
                                                         d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                                     />
                                                 </svg>
                                             </button>
                                         </li>
                                     )
                                 ))}
                             </ul> */}
                            </div>
                        )}

                    </div>
                    <div className="flex justify-start space-x-4">
                        <button
                            type="submit"
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {isEdit ? "Update" : "Save and close"}
                        </button>
                        <button
                            type="button"
                            onClick={handleResetForm}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
            {/* Side view */}
            {isModalOpen && (

                <div
                    className={`fixed inset-y-0 right-0 z-50 w-2/3 md:w-1/3 bg-white dark:bg-gray-800 p-6 shadow-lg transform transition-transform duration-300`}
                >
                    <div className="p-6">
                        <div className="flex justify-between">
                            <h2 className="text-xl font-bold  text-blue-600 mb-4 ">
                                Add TimeSlot
                            </h2>
                            <button
                                // onClick={handleCloseModal}
                                className="text-3xl text-gray-600 dark:text-gray-400 h-6 w-6"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="flex justify-between">
                            <div className="w-1/2 pr-2">
                                <label htmlFor="customStartTime" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                    Start Time
                                </label>
                                <input
                                    type="time"
                                    id="customStartTime"
                                    value={customTime[0]?.customStartTime}
                                    onChange={(e) => setCustomTime([{ ...customTime[0], customStartTime: e.target.value }])}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required={Availability === 'Fixed'}
                                />
                            </div>
                            <div className="w-1/2 pl-2">
                                <label htmlFor="customEndTime" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                    End Time
                                </label>
                                <input
                                    type="time"
                                    id="customEndTime"
                                    value={customTime[0]?.customEndTime}
                                    onChange={(e) => setCustomTime([{ ...customTime[0], customEndTime: e.target.value }])}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required={Availability === 'Fixed'}
                                />
                            </div>
                        </div>
                        <div className="flex space-x-4 justify-start mt-4">
                            <button
                                onClick={() => {
                                    addTimeInput();
                                    setIsModalOpen(false);
                                }}
                                className="px-8 py-2.5 leading-5 text-white transition-colors duration-300 transform bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus:bg-gray-600"
                            >
                                Add
                                {/* {editTaxonomyItem ? "Edit" : "Add"} */}
                            </button>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-8 py-2.5 leading-5 bg-gray-300 rounded-md"
                            >
                                Cancel
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    )
}
